import React, { useState } from 'react';
import Dialog from '@dashboard/components/Dialog';
import { Form } from '@dashboard/components/Formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { webhooksKey } from '@dashboard/lib/queryKeys';
import { WEBHOOK_EVENT } from '@dashboard/enums/webhook-events';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import CopyableText from '@/dashboard/components/CopyableText';
import Link from '@/dashboard/components/Link';
import { eventFieldName, selectedWebhookEvents } from './webhookEventFields';

const WebhookCreateDialog = ({ open, onClose }) => {
  const { sellerId, token } = useAuth();
  const queryClient = useQueryClient();
  const [webhookSecret, setWebhookSecret] = useState(null);
  const [showSecretDialog, setShowSecretDialog] = useState(false);

  const createWebhook = useMutation({
    mutationFn: values =>
      fetcher(`/sellers/${sellerId}/webhooks`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      }),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: webhooksKey(sellerId) });
      toast({
        title: 'Webhook created successfully',
        type: 'success',
      });
      setWebhookSecret(data.webhook.secret);
      setShowSecretDialog(true);
      onClose();
    },
    onError: error => {
      toast({
        title: 'Failed to create webhook',
        description: error?.message || 'Please check your configuration and try again',
        type: 'error',
      });
    },
  });

  const eventOptions = Object.entries(WEBHOOK_EVENT).map(([, value]) => ({
    value,
    label: value,
  }));

  const handleCloseSecretDialog = () => {
    setShowSecretDialog(false);
    setWebhookSecret(null);
  };

  return (
    <>
      <Dialog
        title="Create webhook"
        cancelButton={null}
        confirmButton={null}
        size="xl"
        open={open}
        onCancel={onClose}
        description={
          <>
            Configure a new webhook endpoint to receive real-time notifications when events occur in
            your store.{' '}
            <Link target="_blank" to="/docs/api/v1/webhooks/signature">
              View documentation
            </Link>
          </>
        }
      >
        <Form
          showState={false}
          initialValues={{
            name: '',
            url: '',
            ...Object.fromEntries(
              Object.values(WEBHOOK_EVENT).map(event => [eventFieldName(event), false]),
            ),
            secret: '',
            customHeaders: '',
          }}
          onSubmit={async values => {
            try {
              // Parse custom headers from JSON string
              let customHeaders = {};
              if (values.customHeaders?.trim()) {
                try {
                  customHeaders = JSON.parse(values.customHeaders);
                } catch (e) {
                  throw new Error('Invalid JSON format for custom headers');
                }
              }

              const selectedEvents = selectedWebhookEvents(values);
              if (selectedEvents.length === 0) {
                throw new Error('Please select at least one event type');
              }

              const submitData = {
                name: values.name,
                url: values.url,
                events: selectedEvents,
              };

              if (values.secret?.trim()) {
                submitData.secret = values.secret;
              }

              if (Object.keys(customHeaders).length > 0) {
                submitData.customHeaders = customHeaders;
              }

              const data = await createWebhook.mutateAsync(submitData);
              return { data };
            } catch (error) {
              return { error };
            }
          }}
          fields={() => [
            {
              name: 'name',
              label: 'Webhook name',
              type: 'text',
              required: true,
              placeholder: 'Payment notifications',
              helpText: 'A descriptive name for this webhook endpoint',
            },
            {
              name: 'url',
              label: 'Endpoint URL',
              type: 'url',
              required: true,
              placeholder: 'https://example.com/webhooks',
              helpText: 'The URL where webhook events will be sent',
            },
            {
              name: 'webhookEvents',
              label: 'Event types',
              type: 'fieldgroup',
              helpText: 'Select which events should trigger this webhook',
              fields: eventOptions.map((option, index) => ({
                name: eventFieldName(option.value),
                label: option.label,
                type: 'checkbox',
                noMargin: index !== eventOptions.length - 1,
              })),
            },
            {
              name: 'customHeaders',
              label: 'Custom headers (optional)',
              type: 'textarea',
              placeholder: '{\n "X-Custom-Header": "value"\n}',
              helpText: 'Additional headers to send with webhook requests (JSON format)',
              rows: 4,
            },
          ]}
          formSubmitAlign="end"
          submitButton={{
            label: 'Create webhook',
            appearance: 'primary',
          }}
          cancelButton={{
            label: 'Cancel',
            appearance: 'secondary',
            onClick: onClose,
          }}
        />
      </Dialog>

      <Dialog
        title="Save your webhook secret"
        cancelButton={null}
        confirmButton={{
          label: 'Close',
          appearance: 'primary',
          onClick: handleCloseSecretDialog,
        }}
        size="xl"
        open={showSecretDialog}
        onCancel={handleCloseSecretDialog}
      >
        <div className="space-y-4">
          <Alert variant="default" className="my-4">
            <InformationCircleIcon />
            <AlertTitle>Keep this secret private</AlertTitle>
            <AlertDescription>
              Copy it now, or view it again later from the webhook's actions menu.
            </AlertDescription>
          </Alert>

          <CopyableText text={webhookSecret} label="Webhook secret" />

          <Alert variant="default" className="my-4">
            <InformationCircleIcon />
            <AlertTitle>How to use this secret</AlertTitle>
            <AlertDescription>
              <p>
                View our{' '}
                <Link target="_blank" to="/docs/api/v1/webhooks/signature">
                  webhook documentation
                </Link>{' '}
                to learn how to verify that webhook requests come from Checkout Page by validating
                the signature in the request headers.
              </p>
            </AlertDescription>
          </Alert>
        </div>
      </Dialog>
    </>
  );
};

export default WebhookCreateDialog;
